import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useHttpGetGatewayPriceAreaSummary } from '../../hooks';
import InfoTooltip from '../../components/InfoTooltip/InfoTooltip';
import AsyncSection from './AsyncSection';

const SECTION_INFO = 'Onboarded gateways grouped by their facility’s price area, with the battery capacity and power they add up to.';

const REPORTING_INFO =
    'Gateways that have reported their battery parameters. Gateways that have not reported yet are counted, but contribute nothing to the sums — ' +
    'when some are missing, click the count for a per-gateway breakdown of what is missing.';

const POWER_REPORTED_INFO =
    'How many of the area’s gateways reported each nameplate power figure: inv = inverter rated power, bat = battery max power. The two are reported independently, so each has its own coverage. Gateways whose reading failed are counted as gateways, but contribute nothing to the sums — so a low total means missing data, not a small fleet. When some are missing, click for a per-gateway breakdown.';

const INVERTER_RATED_POWER_INFO =
    'The inverter’s nameplate AC power, as read from the device. On hybrid inverters this includes solar throughput, so it is higher than what the batteries alone can deliver.';

const BATTERY_MAX_POWER_INFO =
    'The battery’s maximum charge/discharge power, as read from the device. The figure that reflects available flexibility.';

// Unit scale for every number in the section. One scale for the whole table, so a
// column cannot flip units row by row; kilo is the default because a single
// gateway's battery is measured in kWh and reads as 0.0100 MWh otherwise. Mega keeps four
// decimals so a single-kW figure (2 kW = 0.0020 MW) does not round away to zero.
export const SCALE_KILO = 'kilo';
export const SCALE_MEGA = 'mega';

const SCALE_OPTIONS = [
    { value: SCALE_KILO, label: 'kWh' },
    { value: SCALE_MEGA, label: 'MWh' },
];

export const formatEnergy = (kwh, scale = SCALE_KILO) => (scale === SCALE_MEGA ? `${(kwh / 1000).toFixed(4)} MWh` : `${Math.round(kwh)} kWh`);
export const formatPower = (kw, scale = SCALE_KILO) => (scale === SCALE_MEGA ? `${(kw / 1000).toFixed(4)} MW` : `${Math.round(kw)} kW`);

const plural = (count, noun) => `${count} ${count === 1 ? noun : `${noun}s`}`;

// Human names for the figures a gateway can fail to report, in the API's order.
const MISSING_FIGURE_LABELS = {
    parameters: 'battery parameters',
    inverter_power: 'inverter rated power',
    battery_power: 'battery max power',
};

// Collapsed by default: the number of price areas is unbounded, and an always-open
// table would push the gateway list below the fold. The header keeps the totals
// visible so collapsing costs no information you would act on at a glance.
const PriceAreaSummary = () => {
    const { areas, notOnboardedCount, isReady, error } = useHttpGetGatewayPriceAreaSummary();
    const [isOpen, setIsOpen] = useState(false);
    const [scale, setScale] = useState(SCALE_KILO);
    // Price areas whose non-reporting gateways are unfolded under their row. Keyed by the
    // row key rather than a boolean so several areas can be open at once.
    const [openReportingAreas, setOpenReportingAreas] = useState(() => new Set());

    const toggleReportingArea = areaKey =>
        setOpenReportingAreas(open => {
            const next = new Set(open);
            if (next.has(areaKey)) {
                next.delete(areaKey);
            } else {
                next.add(areaKey);
            }
            return next;
        });

    // Largest first, so expanding puts the areas carrying the most capacity on top.
    const sortedAreas = useMemo(() => [...areas].sort((a, b) => b.total_energy_capacity_kwh - a.total_energy_capacity_kwh), [areas]);

    // The headline power figure is the inverter's rated power: that is literally what was
    // asked for ("max power from the inverter"), and it is the number the per-gateway card
    // shows under the same label. Battery max power sits beside it in the table, since on
    // hybrid inverters the two diverge and only the battery figure bounds real flexibility.
    const summary = useMemo(() => {
        if (!isReady || error || areas.length === 0) {
            return null;
        }
        const totalKwh = areas.reduce((sum, a) => sum + a.total_energy_capacity_kwh, 0);
        // Defaulted because the portal ships independently of the API: against an API that
        // predates the power sums the field is absent, and an unguarded reduce would put
        // NaN in the header rather than a zero.
        const totalRatedPowerKw = areas.reduce((sum, a) => sum + (a.total_inverter_rated_power_kw ?? 0), 0);
        const totalGateways = areas.reduce((sum, a) => sum + a.gateway_count, 0);
        return (
            `${formatEnergy(totalKwh, scale)} · ${formatPower(totalRatedPowerKw, scale)} ` +
            `across ${plural(areas.length, 'price area')} · ${plural(totalGateways, 'gateway')}`
        );
    }, [areas, isReady, error, scale]);

    return (
        <section className="gateways-overview__areas gateways-overview__panel">
            <h2 className="gateways-overview__areas-header">
                <span className="gateways-overview__areas-heading">
                    <button type="button" className="gateways-overview__areas-toggle" aria-expanded={isOpen} onClick={() => setIsOpen(open => !open)}>
                        <span className="gateways-overview__areas-caret" aria-hidden="true">
                            {isOpen ? '▾' : '▸'}
                        </span>
                        Capacity and power per price area
                    </button>
                    <InfoTooltip tooltip={SECTION_INFO} />
                    {summary ? <span className="gateways-overview__areas-summary">{summary}</span> : null}
                </span>
                {summary ? (
                    <span className="gateways-overview__areas-units" role="group" aria-label="Unit scale">
                        {SCALE_OPTIONS.map(option => (
                            <button
                                key={option.value}
                                type="button"
                                className={`gateways-overview__areas-unit${scale === option.value ? ' gateways-overview__areas-unit--active' : ''}`}
                                aria-pressed={scale === option.value}
                                onClick={() => setScale(option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </span>
                ) : null}
            </h2>

            {isOpen ? (
                <div className="gateways-overview__panel-body">
                    <AsyncSection
                        isReady={isReady}
                        error={error}
                        isEmpty={areas.length === 0}
                        errorText="Could not load the price area summary."
                        emptyText="No onboarded gateways yet."
                    >
                        <div className="gateways-overview__areas-table-wrapper">
                            <table className="gateways-overview__areas-table">
                                <thead>
                                    <tr>
                                        <th>Price area</th>
                                        <th>Gateways</th>
                                        <th>
                                            Reporting
                                            <InfoTooltip tooltip={REPORTING_INFO} />
                                        </th>
                                        <th>Battery capacity</th>
                                        <th>Charge power</th>
                                        <th>Discharge power</th>
                                        <th>
                                            Power reported
                                            <InfoTooltip tooltip={POWER_REPORTED_INFO} />
                                        </th>
                                        <th>
                                            Inverter rated power
                                            <InfoTooltip tooltip={INVERTER_RATED_POWER_INFO} />
                                        </th>
                                        <th>
                                            Battery max power
                                            <InfoTooltip tooltip={BATTERY_MAX_POWER_INFO} />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedAreas.map(area => {
                                        const areaKey = area.price_area ?? 'none';
                                        // Defaulted for the same reason as the power sums: against an API
                                        // that predates the field the cell stays a plain count.
                                        const nonReporting = area.non_reporting_gateways ?? [];
                                        const isUnfolded = openReportingAreas.has(areaKey);
                                        const reportingLabel = `${area.gateways_with_parameters}/${area.gateway_count}`;
                                        const powerLabel =
                                            `inv ${area.gateways_with_inverter_power ?? 0}/${area.gateway_count}` +
                                            ` · bat ${area.gateways_with_battery_power ?? 0}/${area.gateway_count}`;
                                        // A sum nobody feeds is missing data, not a real zero — dash it,
                                        // so "0 kW" can only ever mean a reported zero.
                                        const hasParameters = area.gateways_with_parameters > 0;
                                        const hasInverterPower = (area.gateways_with_inverter_power ?? 0) > 0;
                                        const hasBatteryPower = (area.gateways_with_battery_power ?? 0) > 0;
                                        const missing = <span className="gateways-overview__areas-missing">—</span>;
                                        // Entries from an API that predates `missing` were parameters-only.
                                        const gapsOf = gw => gw.missing ?? ['parameters'];
                                        // Which gaps exist decides which cells become toggles; both open
                                        // the same per-gateway breakdown under the row.
                                        const parameterGaps = nonReporting.some(gw => gapsOf(gw).includes('parameters'));
                                        const powerGaps = nonReporting.some(gw =>
                                            gapsOf(gw).some(figure => figure === 'inverter_power' || figure === 'battery_power')
                                        );
                                        const foldToggle = (label, gaps) =>
                                            gaps ? (
                                                <button
                                                    type="button"
                                                    className="gateways-overview__areas-reporting-toggle"
                                                    aria-expanded={isUnfolded}
                                                    title={
                                                        isUnfolded ? 'Hide which gateways are not reporting' : 'Show which gateways are not reporting'
                                                    }
                                                    onClick={() => toggleReportingArea(areaKey)}
                                                >
                                                    {label}
                                                    <span className="gateways-overview__areas-reporting-caret" aria-hidden="true">
                                                        {isUnfolded ? '▾' : '▸'}
                                                    </span>
                                                </button>
                                            ) : (
                                                label
                                            );
                                        return (
                                            <React.Fragment key={areaKey}>
                                                <tr>
                                                    <td className="gateways-overview__areas-name">{area.price_area ?? 'No price area'}</td>
                                                    <td>{area.gateway_count}</td>
                                                    <td>{foldToggle(reportingLabel, parameterGaps)}</td>
                                                    <td>{hasParameters ? formatEnergy(area.total_energy_capacity_kwh, scale) : missing}</td>
                                                    <td>{hasParameters ? formatPower(area.total_charge_power_kw, scale) : missing}</td>
                                                    <td>{hasParameters ? formatPower(area.total_discharge_power_kw, scale) : missing}</td>
                                                    {/* Same x/total grammar as the Reporting column, one fraction per
                                                    metric — a bare "1 / 1" read as complete when it meant one
                                                    inverter figure and one battery figure out of three gateways. */}
                                                    <td>{foldToggle(powerLabel, powerGaps)}</td>
                                                    <td>
                                                        {hasInverterPower ? formatPower(area.total_inverter_rated_power_kw ?? 0, scale) : missing}
                                                    </td>
                                                    <td>{hasBatteryPower ? formatPower(area.total_battery_max_power_kw ?? 0, scale) : missing}</td>
                                                </tr>
                                                {/* One line per incomplete gateway: the name, then a plain
                                                sentence naming what it has not reported. All of them live in
                                                one cell with its own scroller — an area can be short a few
                                                hundred gateways, and a row per gateway pushed every area
                                                below it off the screen. */}
                                                {isUnfolded && (
                                                    <tr className="gateways-overview__areas-drilldown">
                                                        <td colSpan={9}>
                                                            <ul className="gateways-overview__areas-drilldown-list">
                                                                {nonReporting.map(gw => (
                                                                    <li key={`${areaKey}:${gw.gateway_id}`}>
                                                                        <Link className="gw-link" to={`/gateways/${gw.device_id}`}>
                                                                            {gw.name ?? gw.gateway_id}
                                                                        </Link>
                                                                        {gw.reference ? (
                                                                            <span className="gateways-overview__areas-drilldown-ref">{` (${gw.reference})`}</span>
                                                                        ) : null}
                                                                        <span className="gateways-overview__areas-drilldown-gaps">
                                                                            {` — not reporting ${gapsOf(gw)
                                                                                .map(figure => MISSING_FIGURE_LABELS[figure] ?? figure)
                                                                                .join(', ')}`}
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {notOnboardedCount > 0 && (
                                <p className="gateways-overview__areas-note">
                                    {plural(notOnboardedCount, 'gateway')} not onboarded (no price area yet)
                                </p>
                            )}
                        </div>
                    </AsyncSection>
                </div>
            ) : null}
        </section>
    );
};

export default PriceAreaSummary;
